import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { userService } from '../services/user.service';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { User, Mail, Plus, Loader2, Home, Phone, Edit2, Check, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Customer, CustomerAddress, CreateAddressRequest } from '../types/user.types';
import type { Order } from '../types/order.types';
import { orderService } from '../services/order.service';
import { AddressForm } from '../components/profile/AddressForm';
import { AddressList } from '../components/profile/AddressList';
import { OrderHistoryList } from '../components/profile/OrderHistoryList';

export const UserProfilePage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();

  // Data state
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [shippingAddresses, setShippingAddresses] = useState<CustomerAddress[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Phone editing state
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [isSavingPhone, setIsSavingPhone] = useState(false);

  // Address form state
  const [isAddingAddress, setIsAddingAddress] = useState<boolean>(false);
  const [editingAddress, setEditingAddress] = useState<CustomerAddress | null>(null);
  const [addressForm, setAddressForm] = useState<CreateAddressRequest>({
    type: 'shipping',
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    label: '',
    name: '',
    phone: '',
    is_default: false
  });

  // Silently ensure customer record exists, then load data
  useEffect(() => {
    const init = async () => {
      if (!user?.sub || !user?.email) {
        setIsLoading(false);
        return;
      }

      try {
        // Try to get existing customer data
        let customerData = await userService.getCustomer(user.sub).catch(() => null);

        // If none found, silently create one
        if (!customerData) {
          customerData = await userService.createCustomer(user.sub, user.email, user.name).catch(() => null);
        }

        if (customerData) {
          setCustomer(customerData);
          setPhoneInput(customerData.phone || '');
        }

        // Always attempt to load addresses and orders in parallel
        const [addressesData, ordersData] = await Promise.all([
          userService.getAddresses(user.sub).catch(() => [] as CustomerAddress[]),
          orderService.getOrders({ customer_email: user.email }).catch(() => ({ orders: [] as Order[] })),
        ]);

        setShippingAddresses(addressesData.filter(a => a.type === 'shipping'));
        setOrders(ordersData.orders || []);
      } catch (err) {
        console.error('Profile init error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user?.sub, user?.email, user?.name]);

  const handleSavePhone = async () => {
    if (!user?.sub) return;

    setIsSavingPhone(true);
    try {
      const updatedCustomer = await userService.updateCustomer(user.sub, { phone: phoneInput });
      setCustomer(updatedCustomer);
      setIsEditingPhone(false);
      showSuccess('Phone number updated successfully');
    } catch {
      showError('Failed to update phone number');
    } finally {
      setIsSavingPhone(false);
    }
  };

  // Address handlers — use user.sub as the customer identifier per the API spec
  const handleAddressSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.sub) return;

    try {
      const newAddress = await userService.createAddress(user.sub, addressForm);
      setShippingAddresses(prev => [...prev, newAddress]);
      resetAddressForm();
    } catch {
      showError('Failed to add address');
    }
  };

  const handleAddressUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.sub || !editingAddress) return;

    try {
      const updated = await userService.updateAddress(user.sub, editingAddress.id, addressForm);
      setShippingAddresses(prev => prev.map(a => a.id === updated.id ? updated : a));
      resetAddressForm();
    } catch {
      showError('Failed to update address');
    }
  };

  const handleDeleteAddress = async (addressId: string) => {
    if (!user?.sub) return;
    if (!confirm('Delete this address?')) return;

    try {
      await userService.deleteAddress(user.sub, addressId);
      setShippingAddresses(prev => prev.filter(a => a.id !== addressId));
    } catch {
      showError('Failed to delete address');
    }
  };

  const startEditAddress = (address: CustomerAddress) => {
    setEditingAddress(address);
    setAddressForm({
      type: address.type,
      line1: address.line1,
      line2: address.line2 || '',
      city: address.city,
      state: address.state || '',
      postal_code: address.postal_code || '',
      country: address.country,
      label: address.label || '',
      name: address.name || '',
      phone: address.phone || '',
      is_default: address.is_default
    });
    setIsAddingAddress(false);
  };

  const resetAddressForm = () => {
    setIsAddingAddress(false);
    setEditingAddress(null);
    setAddressForm({
      type: 'shipping',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: '',
      label: '',
      name: '',
      phone: '',
      is_default: false
    });
  };

  // Loading spinner
  if (isLoading) {
    return (
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex flex-col items-center justify-center gap-4">
        <Loader2 className="size-8 animate-spin text-primary" />
        <span className="text-gray-400 font-black uppercase tracking-widest text-xs">Loading profile...</span>
      </div>
    );
  }

  // Not authenticated guard
  if (!user) {
    return <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-20 flex justify-center text-gray-400 font-black uppercase tracking-widest text-xs">Please sign in to view your profile.</div>;
  }

  // ─── Full Profile UI (always rendered, no create-profile gate) ───────────────
  return (
    <div className="max-w-[1280px] mx-auto px-6 lg:px-10 py-6 md:py-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-12">
        <div className="space-y-1">
          <h1 className="text-4xl md:text-5xl font-black tracking-tight text-[#111418] dark:text-white uppercase leading-none">My Profile</h1>
          <p className="text-gray-500 font-medium">Manage your addresses and view your order history.</p>
        </div>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-black text-[#111418] dark:text-white uppercase tracking-widest py-2 px-4 rounded-xl border border-border/40 bg-white dark:bg-black shadow-sm hover:shadow-md transition-shadow"
        >
          <Home className="size-4" /> Return Home
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 md:gap-16">
        {/* ── Left Sidebar ─────────────────────────────────────────────────── */}
        <div className="lg:col-span-4 space-y-10">
          {/* Profile card */}
          <div className="flex items-start gap-5 p-7 bg-white dark:bg-black rounded-3xl border border-border/40 shadow-sm">
            <div className="size-16 shrink-0 rounded-2xl bg-gray-100 dark:bg-gray-900 border border-border/40 flex items-center justify-center text-gray-400">
              <User className="size-8" />
            </div>
            <div className="min-w-0 w-full space-y-3">
              <div>
                <h2 className="text-lg font-black text-[#111418] dark:text-white uppercase tracking-tight truncate leading-tight">{user.name || 'User'}</h2>
                <div className="flex items-center gap-1.5 text-gray-400 mt-0.5">
                  <Mail className="size-3.5 shrink-0" />
                  <span className="text-sm font-medium truncate">{user.email}</span>
                </div>
              </div>

              <div className="pt-3 border-t border-border/40">
                {isEditingPhone ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={phoneInput}
                      onChange={(e) => setPhoneInput(e.target.value)}
                      placeholder="e.g. +91 98765 43210"
                      className="h-8 text-xs font-medium px-2 rounded-lg"
                      autoFocus
                    />
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.preventDefault(); handleSavePhone(); }}
                      disabled={isSavingPhone}
                      className="size-8 p-0 shrink-0 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-500/10 rounded-lg"
                      type="button"
                    >
                      {isSavingPhone ? <Loader2 className="size-3 animate-spin" /> : <Check className="size-3.5" />}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsEditingPhone(false);
                        setPhoneInput(customer?.phone || '');
                      }}
                      className="size-8 p-0 shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg"
                      type="button"
                    >
                      <X className="size-3.5" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between group">
                    <div className="flex items-center gap-1.5 text-gray-500 dark:text-gray-400">
                      <Phone className="size-3.5 shrink-0" />
                      <span className="text-sm font-medium">
                        {customer?.phone || 'Add a phone number'}
                      </span>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => { e.preventDefault(); setIsEditingPhone(true); }}
                      className="size-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                      type="button"
                    >
                      <Edit2 className="size-3 text-gray-400" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Saved Addresses */}
          <div className="space-y-5">
            <div className="flex items-center justify-between relative">
              <h3 className="text-lg font-black uppercase tracking-tight text-[#111418] dark:text-white">Saved Addresses</h3>
              <Button
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  setEditingAddress(null);
                  setAddressForm({
                    type: 'shipping',
                    line1: '',
                    line2: '',
                    city: '',
                    state: '',
                    postal_code: '',
                    country: '',
                    label: '',
                    name: '',
                    phone: '',
                    is_default: false
                  });
                  setIsAddingAddress(true);
                }}
                className="h-9 px-4 rounded-xl font-black text-[10px] uppercase tracking-widest bg-[#111418] dark:bg-white text-white dark:text-black shadow-sm absolute right-0 z-50 cursor-pointer pointer-events-auto"
                type="button"
              >
                <Plus className="size-3.5 mr-1" /> Add Address
              </Button>
            </div>

            {/* Inline add/edit form */}
            {(isAddingAddress || editingAddress) && (
              <AddressForm
                type="shipping"
                addressForm={addressForm}
                setAddressForm={setAddressForm}
                editingAddress={editingAddress}
                onSubmit={editingAddress ? handleAddressUpdate : handleAddressSubmit}
                onCancel={resetAddressForm}
              />
            )}

            <AddressList
              addresses={shippingAddresses}
              type="shipping"
              onEdit={startEditAddress}
              onDelete={handleDeleteAddress}
              onAdd={() => { setIsAddingAddress(true); setEditingAddress(null); }}
              isAdding={isAddingAddress}
            />
          </div>
        </div>

        {/* ── Right Column – Order History ─────────────────────────────────── */}
        <div className="lg:col-span-8">
          <h3 className="text-lg font-black uppercase tracking-tight text-[#111418] dark:text-white mb-6">Order History</h3>
          <OrderHistoryList orders={orders} />
        </div>
      </div>
    </div>
  );
};
